import React, { useState, useEffect, useCallback } from "react";
import { Users, UserPlus, Copy, Trash2, Mail, RefreshCw, X } from "lucide-react";
import { inviteClient, listPortalClients, revokeClientAccess, revokeInvite } from "@/lib/api";
import { toast } from "sonner";

export default function ClientManagementPanel({ portalId, portalName }) {
  const [clients, setClients] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [newInvite, setNewInvite] = useState(null); // {invite_code, email}

  const load = useCallback(async () => {
    if (!portalId) return;
    setLoading(true);
    try {
      const data = await listPortalClients(portalId);
      setClients(data.clients || []);
      setPendingInvites(data.pending_invites || []);
    } catch {
      toast.error("Could not load client list");
    } finally {
      setLoading(false);
    }
  }, [portalId]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async () => {
    setInviting(true);
    try {
      const result = await inviteClient(portalId, inviteEmail.trim() || null);
      setNewInvite(result);
      setInviteEmail("");
      await load();
      toast.success("Invite created");
    } catch {
      toast.error("Could not create invite");
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (email) => {
    try {
      await revokeClientAccess(portalId, email);
      toast.success(`Access revoked for ${email}`);
      await load();
    } catch {
      toast.error("Could not revoke access");
    }
  };

  const handleRevokeInvite = async (code) => {
    try {
      await revokeInvite(portalId, code);
      toast.success("Invite revoked");
      await load();
    } catch {
      toast.error("Could not revoke invite");
    }
  };

  const copyInviteLink = (code) => {
    const registrationUrl = `${window.location.origin}/?invite=${code}`;
    navigator.clipboard.writeText(registrationUrl).then(() => toast.success("Invite link copied!"));
  };

  const registrationBase = `${window.location.origin}/?invite=`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-[#119996]" />
          <h3 className="text-[13px] font-semibold text-[#dddddd]">
            Client Access — {portalName || portalId}
          </h3>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-1.5 rounded hover:bg-white/10 text-[#7d8b96] hover:text-[#dddddd] transition-colors"
          title="Refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Invite form */}
      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 space-y-3">
        <p className="text-[11px] text-[#7d8b96]">
          Generate an invite link for a client. They register using the link — no HubSpot access required.
          Optionally lock the invite to a specific email address.
        </p>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Mail size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#7d8b96]" />
            <input
              type="email"
              placeholder="client@example.com (optional)"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInvite()}
              className="w-full pl-7 pr-3 py-1.5 rounded bg-[#323c44] border border-white/10 text-[12px] text-[#dddddd] placeholder-[#4a5b68] focus:outline-none focus:border-[#57c1a9]/50"
            />
          </div>
          <button
            onClick={handleInvite}
            disabled={inviting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#50e3c2] hover:bg-[#57c1a9] text-[#323c44] text-[12px] font-medium transition-colors disabled:opacity-50"
          >
            <UserPlus size={12} />
            {inviting ? "Creating…" : "Create invite"}
          </button>
        </div>

        {/* Newly created invite — show once */}
        {newInvite && (
          <div className="flex items-center gap-2 p-2.5 rounded bg-[#50e3c2]/10 border border-[#57c1a9]/30">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-[#7d8b96] mb-0.5">Share this link with your client:</p>
              <p className="text-[11px] text-[#dddddd] font-mono truncate">
                {registrationBase}{newInvite.invite_code}
              </p>
            </div>
            <button
              onClick={() => copyInviteLink(newInvite.invite_code)}
              className="shrink-0 p-1.5 rounded hover:bg-white/10 text-[#119996] transition-colors"
              title="Copy link"
            >
              <Copy size={13} />
            </button>
            <button
              onClick={() => setNewInvite(null)}
              className="shrink-0 p-1.5 rounded hover:bg-white/10 text-[#7d8b96] hover:text-[#7d8b96] transition-colors"
            >
              <X size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Pending invites */}
      {pendingInvites.length > 0 && (
        <div>
          <p className="text-[11px] font-medium text-[#7d8b96] uppercase tracking-wider mb-2">
            Pending invites ({pendingInvites.length})
          </p>
          <div className="space-y-1.5">
            {pendingInvites.map((inv) => (
              <div
                key={inv.invite_code}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-[#dddddd] truncate">
                    {inv.email || <span className="text-[#7d8b96] italic">Open invite</span>}
                  </p>
                  <p className="text-[10px] text-[#4a5b68] font-mono truncate">{inv.invite_code}</p>
                </div>
                <button
                  onClick={() => copyInviteLink(inv.invite_code)}
                  className="shrink-0 p-1.5 rounded hover:bg-white/10 text-[#7d8b96] hover:text-[#dddddd] transition-colors"
                  title="Copy link"
                >
                  <Copy size={12} />
                </button>
                <button
                  onClick={() => handleRevokeInvite(inv.invite_code)}
                  className="shrink-0 p-1.5 rounded hover:bg-white/10 text-[#ef4444]/70 hover:text-[#ef4444] transition-colors"
                  title="Revoke invite"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active clients */}
      <div>
        <p className="text-[11px] font-medium text-[#7d8b96] uppercase tracking-wider mb-2">
          Active clients ({clients.length})
        </p>
        {clients.length === 0 ? (
          <p className="text-[12px] text-[#4a5b68] italic">No clients yet. Create an invite above.</p>
        ) : (
          <div className="space-y-1.5">
            {clients.map((c) => (
              <div
                key={c.email}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10"
              >
                <div className="w-6 h-6 rounded-full bg-[#50e3c2]/20 flex items-center justify-center shrink-0">
                  <span className="text-[10px] font-semibold text-[#119996]">
                    {c.email[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-[#dddddd] truncate">{c.email}</p>
                  {c.created_at && (
                    <p className="text-[10px] text-[#4a5b68]">
                      Joined {new Date(c.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleRevoke(c.email)}
                  className="shrink-0 p-1.5 rounded hover:bg-white/10 text-[#7d8b96] hover:text-[#ef4444] transition-colors"
                  title="Revoke access"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
